import { useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea/Textarea";
import { useDeregisterCDM } from "features/cdm/cdm/hooks/useDeregisterCDM";

import style from "./DeregisterPatientDialog.module.css";

// CDM dataset Ref 71 "Retire a Patient" reasons. Enum-locked with BE — these
// exact values are sent verbatim; the HL7 X0337-1..5 codes are added server-side.
const DEREGISTER_REASON_OPTIONS = [
  { label: "Died", value: "DIED" },
  { label: "Moved to Nursing Home", value: "MOVED_TO_NURSING_HOME" },
  { label: "Moved Practice", value: "MOVED_PRACTICE" },
  { label: "Patient Opt-out", value: "PATIENT_OPT_OUT" },
  { label: "Other", value: "OTHER" },
];

const DEFAULT_REASON = "DIED";
const OTHER_REASON = "OTHER";
// Spec X0337-9 "Other Reason for Deregistration": free text, max 30 characters,
// conditionally mandatory when reason === Other.
const OTHER_REASON_MAX_LENGTH = 30;

export const DeregisterPatientDialog = ({ isOpen, onClose, patientId }) => {
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [otherReason, setOtherReason] = useState("");

  const { mutate, isPending, error, reset } = useDeregisterCDM({ patientId });

  const isOtherReason = reason === OTHER_REASON;
  const isOtherReasonMissing = isOtherReason && !otherReason.trim();

  const handleClose = () => {
    if (isPending) return;

    reset();
    setReason(DEFAULT_REASON);
    setOtherReason("");
    onClose();
  };

  const handleConfirm = () => {
    mutate(
      { reason, otherReason: isOtherReason ? otherReason.trim() : null },
      {
        onSuccess: handleClose,
      }
    );
  };

  const errorMessage =
    error?.response?.data?.message || error?.message || null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Deregister patient from CDM"
      width={460}
    >
      <div className={style.body}>
        <p className={style.description}>
          This removes the patient from the Chronic Disease Management
          programme. Select the reason for deregistration.
        </p>

        <label className={style.label}>Reason</label>
        <Select
          options={DEREGISTER_REASON_OPTIONS}
          value={reason}
          onChange={setReason}
          disabled={isPending}
        />

        {!!isOtherReason && (
          <>
            <label className={style.label}>Other reason</label>
            <Textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              maxLength={OTHER_REASON_MAX_LENGTH}
              disabled={isPending}
              placeholder="Specify the reason"
            />
            <p className={style.description}>
              {otherReason.length}/{OTHER_REASON_MAX_LENGTH} characters
            </p>
          </>
        )}

        {!!errorMessage && <p className={style.error}>{errorMessage}</p>}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isPending || !reason || isOtherReasonMissing}
        >
          {isPending ? "Deregistering…" : "Deregister"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
